import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: '#F6F8FA', card: '#FFFFFF', border: '#D0D7DE', borderHover: '#0969DA',
  text: '#1A1F2E', textMuted: '#57606A', textLight: '#8B949E',
  blue: '#0969DA', green: '#1A7F37', red: '#CF222E', orange: '#9A6700', purple: '#6639BA',
  greenBg: '#DAFBE1', redBg: '#FFEBE9', blueBg: '#DDF4FF', orangeBg: '#FFF8C5',
};

const CAPABILITY_COLORS = {
  'refactor': C.blue, 'generate-code': C.blue,
  'test-write': C.green, 'test-run': C.green,
  'scan': C.orange, 'security-scan': C.orange,
  'score': C.purple, '7rs-assess': C.purple,
  'generate-terraform': C.red, 'generate-cdk': C.red,
};

// ── Agent Card ───────────────────────────────────────────────────────────────
function AgentCard({ agent, onEdit, onDelete, onToggle }) {
  const statusColor = agent.status === 'active' ? C.green : agent.status === 'inactive' ? C.textLight : C.red;
  const stats = agent.stats || {};

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 20, transition: 'all 0.15s', position: 'relative',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Status indicator */}
      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
        <span style={{ fontSize: 11, color: statusColor, fontWeight: 600 }}>{agent.status}</span>
      </div>

      {/* Name + version */}
      <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: '0 0 4px' }}>
        {agent.display_name}
      </h3>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
        {agent.name} · v{agent.version}
      </div>

      {/* Capabilities */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {(agent.capabilities || []).map(cap => (
          <span key={cap} style={{
            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
            background: (CAPABILITY_COLORS[cap] || C.textMuted) + '18',
            color: CAPABILITY_COLORS[cap] || C.textMuted,
          }}>{cap}</span>
        ))}
      </div>

      {/* Stacks */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {(agent.stacks || []).map((s, i) => (
          <span key={i} style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 4,
            background: C.bg, color: C.text, fontWeight: 600,
            border: `1px solid ${C.border}`,
          }}>
            {s.language}{s.frameworks?.length ? ` / ${s.frameworks[0]}` : ''}
            {s.frameworks?.length > 1 && ` +${s.frameworks.length - 1}`}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
        <span>🔄 {stats.total_executions || 0} runs</span>
        <span>✅ {stats.success_rate || 0}%</span>
        <span>⏱ {stats.avg_duration_ms ? `${(stats.avg_duration_ms / 1000).toFixed(1)}s` : '—'}</span>
      </div>

      {/* Image URI */}
      <div style={{ fontSize: 10, color: C.textLight, fontFamily: 'monospace', marginBottom: 8,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        📦 {agent.image_uri}
      </div>

      {/* Pipeline Stage */}
      {agent.pipeline_stage && (
        <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>
          🔗 Stage: <strong style={{ color: C.blue }}>{agent.pipeline_stage}</strong>
          {agent.default_task && <> · task: <code style={{ fontSize: 10 }}>{agent.default_task}</code></>}
        </div>
      )}

      {/* LLM info */}
      <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 12 }}>
        🧠 LLM: Kiro CLI (Claude Sonnet 4.6)
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => onEdit(agent)} style={btnStyle}>Edit</button>
        <button onClick={() => onToggle(agent)} style={{
          ...btnStyle,
          color: agent.status === 'active' ? C.orange : C.green,
          borderColor: agent.status === 'active' ? C.orange : C.green,
        }}>
          {agent.status === 'active' ? 'Disable' : 'Enable'}
        </button>
        <button onClick={() => onDelete(agent)} style={{ ...btnStyle, color: C.red, borderColor: C.red }}>
          Delete
        </button>
      </div>
    </div>
  );
}

const btnStyle = {
  fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 6,
  border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer',
  color: C.text, transition: 'all 0.1s',
};

// ── Add/Edit Form ────────────────────────────────────────────────────────────
function AgentForm({ agent, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', display_name: '', version: '1.0.0', image_uri: '',
    capabilities: '', stacks_language: '', stacks_frameworks: '',
    cpu: '500m', memory: '1Gi', timeout: '300', description: '', status: 'active',
    pipeline_stage: '', default_task: '',
  });

  useEffect(() => {
    if (agent) {
      const stacks = agent.stacks || [];
      const res = agent.resources || {};
      setForm({
        name: agent.name || '',
        display_name: agent.display_name || '',
        version: agent.version || '1.0.0',
        image_uri: agent.image_uri || '',
        capabilities: (agent.capabilities || []).join(', '),
        stacks_language: stacks[0]?.language || '',
        stacks_frameworks: (stacks[0]?.frameworks || []).join(', '),
        cpu: res.cpu || '500m',
        memory: res.memory || '1Gi',
        timeout: String(res.timeout || 300),
        description: agent.description || '',
        status: agent.status || 'active',
        pipeline_stage: agent.pipeline_stage || '',
        default_task: agent.default_task || '',
      });
    }
  }, [agent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: form.name.trim(),
      display_name: form.display_name.trim() || form.name.trim(),
      version: form.version.trim(),
      image_uri: form.image_uri.trim(),
      status: form.status,
      capabilities: form.capabilities.split(',').map(s => s.trim()).filter(Boolean),
      stacks: form.stacks_language ? [{
        language: form.stacks_language.trim(),
        frameworks: form.stacks_frameworks.split(',').map(s => s.trim()).filter(Boolean),
      }] : [],
      resources: { cpu: form.cpu, memory: form.memory, timeout: parseInt(form.timeout) || 300 },
      description: form.description,
      pipeline_stage: form.pipeline_stage.trim() || null,
      default_task: form.default_task.trim() || null,
    };
    onSave(data);
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <form onSubmit={handleSubmit} style={{
        background: C.card, borderRadius: 12, padding: 28, width: 500, maxHeight: '80vh',
        overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>
          {agent ? 'Edit Agent' : 'Register New Agent'}
        </h2>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={labelStyle}>Name (unique identifier)</label>
            <input style={inputStyle} value={form.name} disabled={!!agent}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. test-python" required />
          </div>
          <div>
            <label style={labelStyle}>Display Name</label>
            <input style={inputStyle} value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="e.g. Python Test Agent" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Version</label>
              <input style={inputStyle} value={form.version}
                onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                placeholder="1.0.0" required />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Container Image URI</label>
            <input style={inputStyle} value={form.image_uri}
              onChange={e => setForm(f => ({ ...f, image_uri: e.target.value }))}
              placeholder="682663754116.dkr.ecr.us-east-1.amazonaws.com/agents/test-python:1.0.0" required />
          </div>
          <div>
            <label style={labelStyle}>Capabilities (comma-separated)</label>
            <input style={inputStyle} value={form.capabilities}
              onChange={e => setForm(f => ({ ...f, capabilities: e.target.value }))}
              placeholder="test-write, test-run, refactor" required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Language</label>
              <input style={inputStyle} value={form.stacks_language}
                onChange={e => setForm(f => ({ ...f, stacks_language: e.target.value }))}
                placeholder="Python" />
            </div>
            <div>
              <label style={labelStyle}>Frameworks (comma-separated)</label>
              <input style={inputStyle} value={form.stacks_frameworks}
                onChange={e => setForm(f => ({ ...f, stacks_frameworks: e.target.value }))}
                placeholder="Flask, FastAPI, Django" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>CPU</label>
              <input style={inputStyle} value={form.cpu}
                onChange={e => setForm(f => ({ ...f, cpu: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Memory</label>
              <input style={inputStyle} value={form.memory}
                onChange={e => setForm(f => ({ ...f, memory: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Timeout (s)</label>
              <input style={inputStyle} value={form.timeout} type="number"
                onChange={e => setForm(f => ({ ...f, timeout: e.target.value }))} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What this agent does..." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Pipeline Stage
                <span style={{ fontSize: 10, color: '#6E7781', marginLeft: 6 }}>routes to this pipeline step</span>
              </label>
              <select style={inputStyle} value={form.pipeline_stage}
                onChange={e => setForm(f => ({ ...f, pipeline_stage: e.target.value }))}>
                <option value="">— none (utility agent) —</option>
                <option value="coding">coding</option>
                <option value="testing">testing</option>
                <option value="security">security</option>
                <option value="iac">iac</option>
                <option value="discovery">discovery</option>
                <option value="rationalization">rationalization</option>
                <option value="requirements">requirements</option>
                <option value="quality_gate">quality_gate</option>
                <option value="supervisor">supervisor</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Default Task
                <span style={{ fontSize: 10, color: '#6E7781', marginLeft: 6 }}>passed to handler.execute()</span>
              </label>
              <input style={inputStyle} value={form.default_task}
                onChange={e => setForm(f => ({ ...f, default_task: e.target.value }))}
                placeholder="e.g. refactor, run_and_measure, scan" />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={{
            ...btnStyle, padding: '8px 20px', fontSize: 13,
          }}>Cancel</button>
          <button type="submit" style={{
            ...btnStyle, padding: '8px 20px', fontSize: 13, fontWeight: 700,
            background: C.blue, color: '#fff', border: 'none',
          }}>
            {agent ? 'Update Agent' : 'Register Agent'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AgentCatalog() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/agents`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setAgents(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleSave = async (data) => {
    try {
      const isEdit = !!editingAgent;
      const url = isEdit ? `${API_BASE}/api/agents/${editingAgent.id}` : `${API_BASE}/api/agents`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await apiFetch(url.replace(API_BASE, ""), {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `Error: ${res.status}`);
        return;
      }
      setShowForm(false);
      setEditingAgent(null);
      fetchAgents();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.display_name}"?`)) return;
    await apiFetch(`/api/agents/${agent.id}`, { method: 'DELETE' });
    fetchAgents();
  };

  const handleToggle = async (agent) => {
    const newStatus = agent.status === 'active' ? 'inactive' : 'active';
    await apiFetch(`/api/agents/${agent.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchAgents();
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setShowForm(true);
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>🤖 Agent Catalog</h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: '4px 0 0' }}>
            Manage independently deployable agents for the modernization pipeline
          </p>
        </div>
        <button onClick={() => { setEditingAgent(null); setShowForm(true); }} style={{
          padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
          border: 'none', cursor: 'pointer', background: C.blue, color: '#fff',
          boxShadow: '0 2px 8px rgba(9,105,218,0.3)',
        }}>
          + Register Agent
        </button>
      </div>

      {/* Summary bar */}
      <div style={{
        display: 'flex', gap: 20, marginBottom: 20, padding: '12px 16px',
        background: C.card, borderRadius: 8, border: `1px solid ${C.border}`,
      }}>
        <span style={{ fontSize: 12, color: C.textMuted }}>
          <strong style={{ color: C.text }}>{agents.length}</strong> agents registered
        </span>
        <span style={{ fontSize: 12, color: C.green }}>
          <strong>{agents.filter(a => a.status === 'active').length}</strong> active
        </span>
        <span style={{ fontSize: 12, color: C.textLight }}>
          <strong>{agents.filter(a => a.status !== 'active').length}</strong> inactive
        </span>
        <span style={{ fontSize: 12, color: C.textMuted }}>
          <strong>{new Set(agents.flatMap(a => a.capabilities || [])).size}</strong> capabilities
        </span>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 12, background: C.redBg, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.red }}>
          ⚠️ {error} <button onClick={fetchAgents} style={{ marginLeft: 10, ...btnStyle }}>Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading && <div style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Loading agents...</div>}

      {/* Agent grid */}
      {!loading && agents.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
          <p style={{ fontSize: 14 }}>No agents registered yet</p>
          <p style={{ fontSize: 12 }}>Click "Register Agent" to add your first agent to the catalog</p>
        </div>
      )}

      {!loading && agents.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {agents.map(agent => (
            <AgentCard key={agent.id} agent={agent}
              onEdit={handleEdit} onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <AgentForm agent={editingAgent} onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingAgent(null); }} />
      )}
    </div>
  );
}